import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/lenis";

const LINKS = [
  { label: "Services", id: "#services" },
  { label: "Process", id: "#process" },
  { label: "Lifecycle", id: "#lifecycle" },
  { label: "Numbers", id: "#numbers" },
  { label: "Contact", id: "#contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-5 md:h-[72px] md:px-10">
        <button
          data-testid="nav-logo"
          onClick={() => go("#top")}
          className="group flex items-center gap-3 text-left"
        >
          <img
            src="/assets/veritas-seal-192.png"
            alt="Veritas Advisory LLP seal"
            className="h-9 w-9 rounded-full border border-[#B79045]/40 object-cover transition-transform duration-700 group-hover:rotate-45 md:h-10 md:w-10"
          />
          <span className="flex items-baseline gap-2">
            <span className="font-mono text-lg font-bold tracking-[0.18em] text-white transition-colors duration-300 group-hover:text-[#D4AF37] md:text-xl">
              VERITAS
            </span>
            <span className="hidden font-mono text-[10px] tracking-[0.3em] text-neutral-500 sm:block">
              ADVISORY LLP
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.id)}
              className="group relative font-mono text-[11px] uppercase tracking-[0.22em] text-neutral-400 transition-colors duration-300 hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#B79045] transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta-button"
            onClick={() => go("#contact")}
            className="hidden items-center gap-2 border border-[#B79045]/60 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#B79045] hover:text-black sm:inline-flex"
          >
            Get in Touch <ArrowUpRight size={14} />
          </button>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            className="text-neutral-200 lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="overflow-hidden border-t border-white/10 bg-black/90 lg:hidden"
          >
            <div className="flex flex-col gap-5 px-6 py-6">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.id)}
                  className="text-left font-display text-2xl text-neutral-200 transition-colors duration-300 hover:text-[#D4AF37]"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
